import { useMemo, useState } from 'react'
import { Megaphone, Plus, Power, RotateCw, Tablet } from 'lucide-react'
import { Shell, useShellMeta } from '../admin/Shell.js'
import { usePermissions, useToast } from '../admin/primitives.js'
import { useConfirm } from '../shell/confirm.js'
import { Link } from '../router.js'
import { AnnonceModale } from './AnnonceModale.js'
import {
  batterie, CHEMIN_ENROLER, cheminTablette, ecran, estDocParc, etatTablette, FILTRE_TOUTES, filtrer, filtresParc, ilYa, heure,
  libelleBlocage, nomDe, phraseBilan, resume, SALLES, temperature, trier, urlCapture, type Tablette,
} from './tablettes-modele.js'
import { geste, useLectureTablettes } from './tablettes-api.js'
import './tablettes.css'

/**
 * TECHNIQUE › TABLETTES — /technique/tablettes (02/11/2026).
 *
 * Le parc des tablettes murales, lu en direct sur kiosk-api toutes les 10 s :
 * une carte par tablette avec sa dernière capture, son état, sa batterie et
 * sa salle ; les gestes de groupe (écrans, recharger, annonce) ; l'entrée de
 * l'enrôlement par QR code. Maquette validée par Hugo le 02/11/2026.
 */

export function TablettesPage() {
  const meta = useShellMeta()
  const { permissions } = usePermissions()
  const peutPiloter = permissions.includes('tablettes.pilot')
  const peutEnroler = permissions.includes('tablettes.enroll')
  const { lecture, precedente, recharger } = useLectureTablettes('/api/v1/tablettes', estDocParc, 10_000)
  const doc = lecture?.etat === 'ok' ? lecture.data : precedente
  const [filtre, setFiltre] = useState(FILTRE_TOUTES)
  const [annonce, setAnnonce] = useState(false)
  const maintenant = Date.now()
  const toutes = useMemo(() => (doc ? doc.tablettes.filter((t) => typeof t.id === 'string') : []), [doc])
  const visibles = useMemo(() => trier(filtrer(toutes, filtre, maintenant), maintenant), [toutes, filtre, maintenant])
  const r = resume(toutes, maintenant)

  return (
    <Shell active="technique" meta={meta}>
      <main className="lc-page lc-page--wide lc-stagger tb-page">
        <header className="tb-tete">
          <div className="tb-tete__titre">
            <Link className="lc-crumb" href="/technique">← Technique</Link>
            <h1 className="lc-title"><Tablet size={26} aria-hidden="true" /> Tablettes murales</h1>
            <p className="lc-subtitle">
              {doc ? `${r.total} tablette${r.total > 1 ? 's' : ''} · relevé ${ilYa(doc.genere_le)}` : 'Lecture du parc…'}
              {doc && doc.mqtt_connecte === false ? ' · kiosk-api ne joint plus le broker' : ''}
            </p>
          </div>
          <div className="tb-actions">
            {peutPiloter && toutes.length ? <EcransGroupe tablettes={visibles} filtre={filtre} onFait={recharger} /> : null}
            {peutPiloter && toutes.length ? (
              <button type="button" className="lc-btn" onClick={() => setAnnonce(true)}><Megaphone size={16} aria-hidden="true" /> Annonce</button>
            ) : null}
            {peutEnroler ? <Link className="lc-btn lc-btn--primary" href={CHEMIN_ENROLER}><Plus size={16} aria-hidden="true" /> Enrôler une tablette</Link> : null}
          </div>
        </header>

        {lecture?.etat === 'erreur' ? (
          <p className={`tb-bandeau${doc ? '' : ' tb-bandeau--fort'}`} role="status">
            {lecture.message}{doc ? ' Dernier état connu affiché.' : ''}
            {lecture.code === 'TABLETTES_NOT_CONFIGURED' && peutEnroler ? <> <Link href="/settings/integrations/tablettes">Raccorder</Link></> : null}
          </p>
        ) : null}

        {doc ? (
          <>
            <section className="tb-kpis" aria-label="Résumé">
              <Kpi libelle="En ligne" valeur={String(r.enLigne)} detail={`sur ${r.total}`} ton={r.enLigne === r.total ? 'ok' : 'warn'} />
              <Kpi libelle="Écrans allumés" valeur={String(r.ecransAllumes)} detail={[r.veille ? `${r.veille} en veille` : '', r.bloquees ? `${r.bloquees} bloquée${r.bloquees > 1 ? 's' : ''}` : ''].filter(Boolean).join(', ') || '—'} />
              <Kpi libelle="À surveiller" valeur={String(r.surveiller)} detail="hors ligne, batterie, chaleur, hors service" ton={r.surveiller ? 'warn' : 'ok'} />
              <Kpi libelle="Version de l’app" valeur={r.versions.length === 1 ? r.versions[0]! : r.versions.length ? `${r.versions.length} versions` : '—'} detail={r.versions.length > 1 ? r.versions.join(', ') : r.versions.length ? 'la même partout' : ''} />
            </section>

            <div className="tb-chips" role="group" aria-label="Filtrer">
              {filtresParc(toutes, maintenant).map((f) => (
                <button key={f.label} type="button" className={`tb-chip${filtre === f.label ? ' tb-chip--on' : ''}`} aria-pressed={filtre === f.label} onClick={() => setFiltre(f.label)}>
                  {f.label} <span className="tb-chip__n">{f.n}</span>
                </button>
              ))}
            </div>

            {visibles.length ? (
              <section className="tb-grille" aria-label="Tablettes">
                {visibles.map((t) => <Carte key={t.id} t={t} maintenant={maintenant} />)}
              </section>
            ) : (
              <div className="lc-panel tb-vide">
                <p>{toutes.length ? 'Aucune tablette dans ce filtre.' : 'Aucune tablette enrôlée pour l’instant.'}</p>
                {!toutes.length && peutEnroler ? <Link className="lc-btn lc-btn--primary" href={CHEMIN_ENROLER}><Plus size={16} aria-hidden="true" /> Enrôler la première</Link> : null}
              </div>
            )}
          </>
        ) : !lecture ? <p className="lc-hint" aria-busy="true">Lecture du parc…</p> : null}

        {annonce && doc ? <AnnonceModale cible={{ type: 'parc', tablettes: toutes }} onFermer={() => setAnnonce(false)} /> : null}
      </main>
    </Shell>
  )
}

function Kpi({ libelle, valeur, detail, ton }: { libelle: string; valeur: string; detail: string; ton?: 'ok' | 'warn' }) {
  return (
    <div className="tb-kpi">
      <span className="tb-kpi__libelle">{libelle}</span>
      <span className="tb-kpi__ligne"><b className={`tb-kpi__valeur${ton ? ` tb-ton--${ton}` : ''}`}>{valeur}</b><span className="tb-kpi__detail">{detail}</span></span>
    </div>
  )
}

/**
 * Allumer ou éteindre les écrans, recharger les pages, des tablettes affichées :
 * une salle part en `{ salle }`, sinon la liste des tablettes en ligne (50 au plus).
 */
function EcransGroupe({ tablettes, filtre, onFait }: { tablettes: Tablette[]; filtre: string; onFait: () => void }) {
  const toast = useToast()
  const confirm = useConfirm()
  const [envoi, setEnvoi] = useState(false)
  const enLigne = tablettes.filter((t) => t.en_ligne)
  const salle = (SALLES as readonly string[]).includes(filtre) ? filtre : null
  const cible = salle ? { salle } : { ids: enLigne.slice(0, 50).map((t) => t.id) }
  const qui = salle ? `de ${salle}` : filtre === FILTRE_TOUTES ? 'de toutes les tablettes' : `de ${enLigne.length} tablette${enLigne.length > 1 ? 's' : ''}`
  const agir = async (valeur: 'ON' | 'OFF') => {
    if (!enLigne.length) return
    if (valeur === 'OFF' && !(await confirm({
      title: `Éteindre les écrans ${qui} ?`,
      message: 'Les écrans passent en veille. « Rallumer » ou un toucher les rallume.',
      variant: 'standard', confirmLabel: 'Éteindre',
    }))) return
    setEnvoi(true)
    const r = await geste('POST', '/api/v1/tablettes/groupe/reglage/ecran', { cible, valeur })
    setEnvoi(false)
    toast(r.ok ? 'ok' : 'error', r.ok ? phraseBilan(r.data?.['bilan'] as never, valeur === 'ON' ? 'Écrans rallumés' : 'Écrans éteints') : r.message)
    onFait()
  }
  const recharger = async () => {
    setEnvoi(true)
    const r = await geste('POST', '/api/v1/tablettes/groupe/ordre/recharger', { cible })
    setEnvoi(false)
    toast(r.ok ? 'ok' : 'error', r.ok ? phraseBilan(r.data?.['bilan'] as never, 'Pages rechargées') : r.message)
  }
  return (
    <>
      <button type="button" className="lc-btn" disabled={envoi || !enLigne.length} onClick={() => { void agir('OFF') }}><Power size={16} aria-hidden="true" /> Éteindre les écrans</button>
      <button type="button" className="lc-btn" disabled={envoi || !enLigne.length} onClick={() => { void agir('ON') }}>Rallumer</button>
      <button type="button" className="lc-btn lc-btn--icon" disabled={envoi || !enLigne.length} aria-label="Recharger les pages" title="Recharger les pages" onClick={() => { void recharger() }}><RotateCw size={16} aria-hidden="true" /></button>
    </>
  )
}

function Carte({ t, maintenant }: { t: Tablette; maintenant: number }) {
  const e = etatTablette(t, maintenant)
  const b = batterie(t)
  const [sansImage, setSansImage] = useState(false)
  const bloque = t.etat?.bloque === true
  const veille = t.etat?.ecran === 'OFF' && !bloque
  return (
    <Link className={`tb-carte${t.en_ligne === false ? ' tb-carte--off' : ''}`} href={cheminTablette(t.id)} aria-label={`${nomDe(t)} — ${e.texte}`}>
      <div className="tb-carte__ecran">
        {bloque ? (
          <div className="tb-carte__bloque"><b>{libelleBlocage(t.etat?.bloque_style)}</b><span>{t.etat?.bloque_texte ?? ''}</span></div>
        ) : veille ? (
          <div className="tb-carte__veille">Écran en veille</div>
        ) : !sansImage ? (
          <img src={urlCapture(t, 'vignette')} alt="" loading="lazy" decoding="async" onError={() => setSansImage(true)} />
        ) : <div className="tb-carte__veille">Pas de capture</div>}
        <span className={`tb-pastille tb-ton--${e.ton}`}><span className="tb-point" aria-hidden="true" />{e.texte}</span>
        <span className="tb-carte__heure">{heure(t.capture_le)}</span>
      </div>
      <div className="tb-carte__corps">
        <div className="tb-carte__ligne">
          <b className="tb-carte__nom">{nomDe(t)}</b>
          <span className={`tb-salle${t.salle ? '' : ' tb-salle--vide'}`}>{t.salle ?? 'sans salle'}</span>
        </div>
        <div className="tb-jauge" aria-label={`Batterie ${b.texte}`}>
          <span className="tb-jauge__piste"><span className={`tb-jauge__plein tb-fond--${b.ton}`} style={{ width: `${b.pct ?? 0}%` }} /></span>
          <span className="tb-mono">{b.texte}</span>
        </div>
        <div className="tb-carte__faits">
          <span>{temperature(t)}</span><span>{ecran(t)}</span><span>vu {ilYa(t.vu_le, maintenant)}</span>
        </div>
      </div>
    </Link>
  )
}
